import Swiper, { Autoplay, Navigation, Pagination } from 'swiper';
import type { Swiper as SwiperInstance } from 'swiper'; 

// イベント
document.addEventListener('DOMContentLoaded', () => {
  const topEvent = document.querySelector('.top-events') as HTMLElement | null;
  if (!topEvent) return;

  // テンプレートIDごとのSwiperインスタンスを保持するためのオブジェクト
  const swiperInstances: Record<string, SwiperInstance[]> = {};
  
  let currentActiveTemplateId: string | null = null; // 現在表示されているテンプレートIDを保持

  const eventSwitchBtns = topEvent.querySelectorAll<HTMLButtonElement>('.top-event-nav__btn');
  const eventShowArea = document.getElementById('top-event-list') as HTMLElement | null;
  if (!eventShowArea) return;

  // 1. 初期テンプレートIDの決定
  eventSwitchBtns.forEach((eventBtn) => {
    if (eventBtn.classList.contains('is-active')) {
      currentActiveTemplateId = eventBtn.getAttribute('data-template');
    }
  });

  if (!currentActiveTemplateId) return;

  const eventDefaultTemplate = document.getElementById(currentActiveTemplateId) as HTMLTemplateElement | null;
  if (!eventDefaultTemplate) return;

  const eventDefaultTemplateClone = eventDefaultTemplate.content.cloneNode(true) as DocumentFragment;

  // 2. 初期状態の設定 (DOM挿入)
  eventShowArea.innerHTML = '';
  const eventInitialElement = document.createElement('div');
  eventInitialElement.appendChild(eventDefaultTemplateClone);
  eventShowArea.appendChild(eventInitialElement);

  // 3. Swiperの破棄と初期化を管理する関数

  /**
   * 既存のSwiperインスタンスを破棄する関数
   * @param templateId 破棄対象のテンプレートID
   */
  const destroySwipers = (templateId: string) => {
    if (swiperInstances[templateId]) {
      swiperInstances[templateId].forEach(swiper => {
        if (swiper) {
          // true, true でDOMイベントとノードを完全に破棄
          swiper.destroy(true, true); 
        }
      });
      delete swiperInstances[templateId];
    }
  };

  /**
   * コンテナ内のSwiperを初期化または破棄する関数
   * @param container Swiperコンテナを含む要素
   * @param templateId 現在のテンプレートID
   */
  const eventInitializeSwipers = (container: HTMLElement, templateId: string) => {
    // 古いインスタンスを破棄
    destroySwipers(templateId); 

    const eventSwiperContainers = container.querySelectorAll('[data-role="top-event-swiper"]');
    swiperInstances[templateId] = []; // 新しいインスタンス配列を初期化
    
    eventSwiperContainers.forEach((swiperContainer) => {
      const swiperEl = swiperContainer as HTMLElement;
      if (!swiperEl) return;

      // ナビゲーションとページネーション要素を取得
      const nextEl = swiperContainer.querySelector('[data-role="top-event-swiper-nav-next"]') as HTMLElement | null;
      const prevEl = swiperContainer.querySelector('[data-role="top-event-swiper-nav-prev"]') as HTMLElement | null;
      const paginationEl = swiperContainer.querySelector('[data-role="top-event-swiper-nav-pagination"]') as HTMLElement | null;
      
      // スライド数を取得
      const slides = swiperEl.querySelectorAll('.swiper-slide');
      
      // Swiperのデフォルト設定
      const swiperParams = {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 16,
        loop: true,
        speed: 1500,
        breakpoints: {
          1024: {
            slidesPerView: 4,
            spaceBetween: 32,
          },
        },
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        pagination: {
          el: paginationEl,
          clickable: true,
        },
      };

      // 現在のウィンドウ幅に基づいて適切なslidesPerViewを決定
      let currentSlidesPerView = swiperParams.slidesPerView;
      const currentWindowWidth = window.innerWidth;
      
      if (swiperParams.breakpoints) {
        Object.keys(swiperParams.breakpoints)
          .map(Number)
          .sort((a, b) => b - a)
          .forEach(breakpoint => {
            if (currentWindowWidth >= breakpoint) {
              const breakpointSettings = swiperParams.breakpoints![breakpoint];
              if (typeof breakpointSettings !== 'function' && breakpointSettings.slidesPerView !== undefined) {
                  currentSlidesPerView = breakpointSettings.slidesPerView;
              }
            }
          });
      }

      // --- destroy条件のチェック ---
      if (slides.length <= currentSlidesPerView) {
        // スライド数が現在のslidesPerView以下の場合、Swiperを初期化せず、ナビゲーションを非表示にする
        console.log(`Swiper (Template: ${templateId}) skipped init/destroyed due to slides.length (${slides.length}) <= slidesPerView (${currentSlidesPerView})`);
        
        // ナビゲーションとページネーションを非表示にする
        if (nextEl) nextEl.style.display = 'none';
        if (prevEl) prevEl.style.display = 'none';
        if (paginationEl) paginationEl.style.display = 'none';

        // swiperContainerに is-destroy クラスを追記
        swiperContainer.classList.add('is-destroy');

        return; // Swiperの初期化をスキップ
      }
      
      // 初期化する場合はナビゲーションを表示状態に戻す
      // スタイルシートで display: none が設定されている場合は、適切な表示値 ('block', 'flex' など) に変更してください。
      if (nextEl) nextEl.style.display = ''; 
      if (prevEl) prevEl.style.display = '';
      if (paginationEl) paginationEl.style.display = '';

      // swiperContainerから is-destroy クラスを削除
      swiperContainer.classList.remove('is-destroy');


      // --- Swiperの初期化 ---
      const newSwiper = new Swiper(swiperEl, swiperParams);
      swiperInstances[templateId].push(newSwiper);
    });
  };

  // 4. 初期状態の Swiper を初期化
  eventInitializeSwipers(eventInitialElement, currentActiveTemplateId);

  // ------------------------------------------
  
  // 5. 要素切り替え (タブ切り替え) イベントリスナー
  const eventSwitchArea = document.querySelector('.top-event-nav') as HTMLElement | null;
  if (!eventSwitchArea) return;

  eventSwitchArea.addEventListener('click', (e: Event) => {
    const eventSwitch = e.target as HTMLElement;
    if (!eventSwitch || !eventSwitch.hasAttribute('data-template')) return;

    const eventActiveTemplate = eventSwitch.getAttribute('data-template');
    if (!eventActiveTemplate || eventActiveTemplate === currentActiveTemplateId) return;

    currentActiveTemplateId = eventActiveTemplate; // 現在のアクティブIDを更新

    const eventShowTemplate = document.getElementById(eventActiveTemplate) as HTMLTemplateElement | null;
    if (!eventShowTemplate) return;

    const eventShowTemplateClone = eventShowTemplate.content.cloneNode(true) as DocumentFragment;

    // ボタン切り替え
    eventSwitchBtns.forEach((btn) => {
      if (btn === eventSwitch) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-selected', 'false');
      }
    });

    // 中身切り替え (DOMの入れ替え)
    eventShowArea.innerHTML = '';
    const eventNewElement = document.createElement('div');
    eventNewElement.appendChild(eventShowTemplateClone);
    eventShowArea.appendChild(eventNewElement);

    // 新しい要素内の Swiper を初期化
    eventInitializeSwipers(eventNewElement, currentActiveTemplateId);
  });
  
  // ------------------------------------------
  
  // 6. リサイズ時の処理
  
  let resizeTimer: number;

  const handleResize = () => {
    if (!currentActiveTemplateId) return;

    const currentTemplateElement = eventShowArea.querySelector('div'); // 現在表示されているコンテナを取得
    if (!currentTemplateElement) return;

    // 現在のテンプレートのSwiperインスタンスを破棄してから、再初期化/破棄判定を実行
    // これにより、ブレークポイントをまたいだ際にslidesPerViewが変更され、条件が再評価されます。
    destroySwipers(currentActiveTemplateId); 
    eventInitializeSwipers(currentTemplateElement, currentActiveTemplateId);
  };

  // リサイズイベントにデバウンスをかけて登録
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    // 300ms後に実行することで、リサイズ中の過度な処理実行を防ぐ
    resizeTimer = window.setTimeout(handleResize, 300); 
  });
});

// 特設ページ
let topSpecialSwiper: Swiper | null = null;
let topSpecialSwiperStatus = false;

const swiperSelector = '[data-role="top-special-swiper"]';
const swiperEl = document.querySelector(swiperSelector) as HTMLElement | null;

if (swiperEl) {
  const navNext = document.querySelector('[data-role="top-special-swiper-nav-next"]') as HTMLElement | null;
  const navPrev = document.querySelector('[data-role="top-special-swiper-nav-prev"]') as HTMLElement | null;
  const paginationEl = document.querySelector('[data-role="top-special-swiper-nav-pagination"]') as HTMLElement | null;

  /**
   * destroy時の処理
   */
  const destroySwiper = () => {
    if (topSpecialSwiper) {
      topSpecialSwiper.destroy(false, true);
      topSpecialSwiper = null;
    }
    topSpecialSwiperStatus = false;

    // ★ is-destroy を付与
    swiperEl.classList.add("is-destroy");

    // ナビとページネーションを非表示（CSSでis-destroy時に非表示でもOK）
    navNext?.classList.add("is-hidden");
    navPrev?.classList.add("is-hidden");
    paginationEl?.classList.add("is-hidden");
  };

  /**
   * Swiper生成
   */
  const createSwiper = () => {
    const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
    const isDesktop = window.innerWidth >= 1024;
    const perView = isDesktop ? 3 : 1.2;
    const space = isDesktop ? 32 : 24;
    const centered = isDesktop ? false : true;

    // スライド数が表示数以下ならdestroy
    if (slideCount <= perView) {
      destroySwiper();
      return;
    }

    // すでに生成済みの場合はスキップ
    if (topSpecialSwiperStatus) return;

    // ★ is-destroy 削除
    swiperEl.classList.remove("is-destroy");
    navNext?.classList.remove("is-hidden");
    navPrev?.classList.remove("is-hidden");
    paginationEl?.classList.remove("is-hidden");

    topSpecialSwiper = new Swiper(swiperSelector, {
      modules: [Navigation, Pagination],
      slidesPerView: perView,
      spaceBetween: space,
      centeredSlides: centered,
      centerInsufficientSlides: true,
      speed: 1500,
      navigation: {
        nextEl: '[data-role="top-special-swiper-nav-next"]',
        prevEl: '[data-role="top-special-swiper-nav-prev"]',
      },
      pagination: {
        el: "[data-role='top-special-swiper-nav-pagination']",
        clickable: true,
      },
    });

    topSpecialSwiperStatus = true;
  };

  /**
   * リサイズ処理
   */
  const handleResize = () => {
    const slideCount = swiperEl.querySelectorAll(".swiper-slide").length;
    const isDesktop = window.innerWidth >= 1024;
    const perView = isDesktop ? 3 : 1;

    // スライド不足 → destroy
    if (slideCount <= perView) {
      destroySwiper();
      return;
    }

    // 未生成 → 生成
    if (!topSpecialSwiperStatus) {
      createSwiper();
      return;
    }

    // slidesPerView が変わる → 再生成
    if (topSpecialSwiper?.params.slidesPerView !== perView) {
      destroySwiper();
      createSwiper();
    }
  };

  window.addEventListener("load", createSwiper);
  window.addEventListener("resize", handleResize);
}

// エリア
document.addEventListener('DOMContentLoaded', () => {
  const swiperElement = document.querySelector<HTMLElement>('[data-role="top-area-swiper"]');

  if (!swiperElement) return;

  const areaSwiper = new Swiper(swiperElement, {
    modules: [Autoplay, Navigation],
    navigation: {
      nextEl: '[data-role="top-area-swiper-nav-next"]',
      prevEl: '[data-role="top-area-swiper-nav-prev"]',
    },
    pagination: {
      el: '[data-role="top-area-swiper-nav-pagination"]',
      clickable: true,
    },
    slidesPerView: 1,
    spaceBetween: 16,
    loop: true,
    speed: 3000,
  });

  // SVGマップのクリックイベント
  const areaLinks = document.querySelectorAll<SVGGElement>('.area-map [data-area-id]');

  areaLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();

      const areaId = link.dataset.areaId;
      if (!areaId) return;

      // 全てのaタグから.is-activeを削除し、クリックされたaタグに.is-activeを追加
      areaLinks.forEach((el) => el.classList.remove('is-active'));
      link.classList.add('is-active');

      // 対応するスライドのインデックスを取得
      const slides = swiperElement.querySelectorAll('.swiper-slide');
      let targetIndex = -1;

      slides.forEach((slide, index) => {
        if (slide.getAttribute('data-area-id') === areaId) {
          targetIndex = index - 1;
          console.log(targetIndex);
        }
      });

      if (targetIndex !== -1) {
        areaSwiper.slideToLoop(targetIndex);
      }
    });
  });

  // Swiperのアクティブなスライドに対応するマップエリアをアクティブにする
  const updateActiveMapFromSwiper = (): void => {
    const activeSlide = areaSwiper.slides[areaSwiper.activeIndex];
    const activeAreaId = activeSlide?.getAttribute('data-area-id');

    areaLinks.forEach((link) => {
      if (link.dataset.areaId === activeAreaId) {
        link.classList.add('is-active');
      } else {
        link.classList.remove('is-active');
      }
    });
  };

  // スライド変更時にマップを更新
  areaSwiper.on('slideChange', updateActiveMapFromSwiper);

  // 初回ロード時にアクティブ状態を設定
  updateActiveMapFromSwiper();
});
